"use client";

import React, { createContext, useState, useContext, useEffect } from "react";
import { BASE_URL } from "../config/apiConfig";
import { toast } from "react-toastify";

export const AssetCategoryContext = createContext({
  categories: [],
});

// CHANGED: Added a safe JSON parsing helper to prevent "Unexpected token '<'" crashes.
// This guards against cases where the backend returns an HTML error page (404/500/proxy errors)
// or an empty body instead of valid JSON, which previously caused res.json() to throw.
const safeParseJSON = async (res) => {
  try {
    const text = await res.text();
    if (!text) {
      return { status: res.status, message: "Empty response from server" };
    }
    try {
      return JSON.parse(text);
    } catch (parseErr) {
      return { status: res.status, message: "Invalid response format from server" };
    }
  } catch (readErr) {
    return { status: res.status || 500, message: "Failed to read server response" };
  }
};

export const AssetCategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ── token helper ─────────────────────────────
  const getToken = () => localStorage.getItem("token");

  // ─────────────────────────────────────────────
  // GET ALL CATEGORIES — fetch operation, no toasts
  // CHANGED: Removed toast.error calls — this is a GET/fetch operation, not a user action.
  // ─────────────────────────────────────────────
  const fetchCategories = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/asset-categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (data.status === 200) {
        setCategories(data.data || []);
      } else {
        setError(data.message || "Failed to fetch categories");
        // CHANGED: removed toast.error(data.message || "Failed to fetch categories")
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching categories");
      // CHANGED: removed toast.error("Error fetching categories")
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // CREATE CATEGORY — user action, toasts preserved
  // ─────────────────────────────────────────────
  const createCategory = async (payload) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/asset-categories`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (data.status === 201) {
        await fetchCategories();
        toast.success(data.message || "Category created successfully"); // KEPT: user-initiated action
        return { success: true, message: "Category created successfully" };
      }

      toast.error(data.message || "Failed to create category"); // KEPT: user-initiated action
      return { success: false, message: data.message };
    } catch (err) {
      console.error(err);
      toast.error("Error creating category"); // KEPT: user-initiated action
      return { success: false, message: "Error creating category" };
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // UPDATE CATEGORY — user action, toasts preserved
  // ─────────────────────────────────────────────
  const updateCategory = async (id, payload) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/asset-categories/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await safeParseJSON(res); // CHANGED: safe parsing instead of res.json()

      if (data.status === 200) {
        await fetchCategories();
        toast.success(data.message || "Category updated successfully"); // KEPT: user-initiated action
        return { success: true, message: "Category updated successfully" };
      }

      toast.error(data.message || "Failed to update category"); // KEPT: user-initiated action
      return { success: false, message: data.message };
    } catch (err) {
      console.error(err);
      toast.error("Error updating category"); // KEPT: user-initiated action
      return { success: false, message: "Error updating category" };
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // DELETE CATEGORY — user action, toasts preserved
  // ─────────────────────────────────────────────
  const deleteCategory = async (id, payload) => {
  setLoading(true);

  try {
    const token = getToken();

    const res = await fetch(`${BASE_URL}/asset-categories/${id}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const data = await safeParseJSON(res);

    if (data.status === 200) {
      await fetchCategories();
      toast.success(data.message || "Category deleted successfully");
      return { success: true };
    }

    toast.error(data.message || "Failed to delete category");
    return { success: false, message: data.message };
  } catch (err) {
    console.error(err);
    toast.error("Error deleting category");
    return { success: false };
  } finally {
    setLoading(false);
  }
};

  // ─────────────────────────────────────────────
  // ACTIVE ONLY FILTER (helper)
  // ─────────────────────────────────────────────
  const activeCategories = categories.filter(
    (c) => c.active === true || c.status === "active"
  );

  // ─────────────────────────────────────────────
  // INIT LOAD
  // ─────────────────────────────────────────────
  useEffect(() => {
    const token = getToken();
    if (token) fetchCategories();
  }, []);

  return (
    <AssetCategoryContext.Provider
      value={{
        categories,
        activeCategories,
        loading,
        error,
        fetchCategories,
        createCategory,
        updateCategory,
        deleteCategory,
      }}
    >
      {children}
    </AssetCategoryContext.Provider>
  );
};

// ─────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────
export const useAssetCategories = () =>
  useContext(AssetCategoryContext);